import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export interface Benefit {
  id: string;
  tenant_id: string;
  name: string;
  type:
    | 'health_insurance'
    | 'dental'
    | 'vision'
    | 'life_insurance'
    | 'retirement'
    | 'wellness'
    | 'transportation'
    | 'other';
  description: string | null;
  provider: string | null;
  status: 'active' | 'inactive';
  effective_date: string | null;
  expiry_date: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BenefitEnrollment {
  id: string;
  tenant_id: string;
  benefit_id: string;
  employee_id: string;
  enrolled_at: string;
  status: 'active' | 'inactive' | 'pending';
  created_at: string;
  updated_at: string;
}

export interface CreateBenefitInput {
  name: string;
  type?: Benefit['type'];
  description?: string | null;
  provider?: string | null;
  effective_date?: string | null;
  expiry_date?: string | null;
}

export const benefitKeys = {
  all: ['benefits'] as const,
  list: (tenantId: string) => [...benefitKeys.all, 'list', tenantId] as const,
  enrollments: (tenantId: string) => [...benefitKeys.all, 'enrollments', tenantId] as const,
} as const;

export function useBenefits(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: benefitKeys.list(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('benefits')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Benefit[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateBenefit(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBenefitInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('benefits')
        // @ts-ignore dynamic
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as Benefit;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: benefitKeys.list(tenantId) }),
  });
}

export function useUpdateBenefit(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<CreateBenefitInput> & { id: string; status?: Benefit['status'] }) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('benefits')
        // @ts-ignore dynamic
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as Benefit;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: benefitKeys.list(tenantId) }),
  });
}

export function useDeleteBenefit(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('benefits')
        // @ts-ignore dynamic
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: benefitKeys.list(tenantId) }),
  });
}

export function useBenefitEnrollments(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: benefitKeys.enrollments(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('benefit_enrollments')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('enrolled_at', { ascending: false });
      if (error) throw error;
      return data as BenefitEnrollment[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useEnrollBenefit(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (benefitId: string) =>
      apiFetch<BenefitEnrollment>(supabase, `addons/benefits/${benefitId}/enroll`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: benefitKeys.enrollments(tenantId) }),
  });
}
